import React, { ReactNode, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { Header } from './Header';
import { Sidebar, menuItems } from './Sidebar';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Longest matching path wins, so /members/123 still highlights /members.
  const activeItem =
    [...menuItems]
      .sort((a, b) => b.path.length - a.path.length)
      .find((item) => location.pathname.startsWith(item.path))?.id || 'create-lead';

  const sidebarWidth = sidebarCollapsed ? 60 : 200;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header
        userName={user?.full_name || `${user?.first_name} ${user?.last_name}`}
        subClinic={user?.sub_clinic}
        onLogout={logout}
      />
      <Box sx={{ display: 'flex', flexGrow: 1, pt: '60px', position: 'relative' }}>
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeItem={activeItem}
          onNavigate={(path: string) => navigate(path)}
        />
        <Box
          component="main"
          // Every screen inside the shell is Hebrew, so the direction belongs
          // here rather than on each page. Three of them had been written
          // without it - profile, forgot password and reset password - and
          // rendered left aligned, with the full stop landing at the start of
          // the sentence.
          dir="rtl"
          sx={{
            flexGrow: 1,
            p: 3,
            ml: `${sidebarWidth}px`,
            transition: 'margin-left 0.3s ease',
            width: '100%',
            boxSizing: 'border-box',
            maxWidth: `calc(100% - ${sidebarWidth}px)`,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
